import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FolderPlus, Loader2 } from "lucide-react";

const NEW_PROJECT = "__new__";

/* Phase 1 Save to Project: attach the exact variant to an existing project or create
   one inline — reuses the existing project + line APIs; no separate cart model. */
export function SaveToProjectDialog({ open, onOpenChange, variant }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [np, setNp] = useState({ name: "", projected_sqft: "", waste_pct: "15" });
  const [area, setArea] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setProjectId("");
      setNp({ name: "", projected_sqft: "", waste_pct: "15" });
      setArea("");
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }
  }, [open]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let pid = projectId;
      if (projectId === NEW_PROJECT) {
        const { data } = await api.post("/projects", {
          name: np.name,
          projected_sqft: parseFloat(np.projected_sqft),
          waste_pct: parseInt(np.waste_pct || "15", 10),
        });
        pid = data.id;
      }
      await api.post(`/projects/${pid}/lines`, { variant_id: variant.id, area });
      toast.success("Saved to project — quantities calculated");
      onOpenChange(false);
      navigate(`/dealer/projects/${pid}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isNew = projectId === NEW_PROJECT;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Save to project</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            {variant?.label} — attach it to a project to calculate quantities, sample, reserve, and order from one place.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="mt-2 space-y-5" data-testid="save-to-project-form">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project *</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="save-project-select" className="h-11 bg-white"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent className="bg-white">
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`save-project-option-${p.id}`}>{p.name}</SelectItem>
                ))}
                <SelectItem value={NEW_PROJECT} data-testid="save-project-new">+ New project</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isNew && (
            <div className="space-y-4 rounded-md border border-border bg-background p-4" data-testid="save-new-project-fields">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project name *</Label>
                <Input required value={np.name} onChange={(e) => setNp((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Coral Gables Residence" data-testid="save-new-project-name" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Projected sqft *</Label>
                  <Input required type="number" step="0.1" min="1" value={np.projected_sqft}
                    onChange={(e) => setNp((f) => ({ ...f, projected_sqft: e.target.value }))}
                    data-testid="save-new-project-sqft" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Overage %</Label>
                  <Input type="number" min="0" max="100" step="1" value={np.waste_pct}
                    onChange={(e) => setNp((f) => ({ ...f, waste_pct: e.target.value }))}
                    data-testid="save-new-project-overage" className="h-11 bg-white focus-visible:ring-terracotta" />
                  <p className="text-[10px] text-muted-foreground">Recommended: 15% — any value allowed, including 0%</p>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Area / room (optional)</Label>
            <Input value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Master Bath"
              data-testid="save-project-area" className="h-11 bg-white focus-visible:ring-terracotta" />
          </div>
          <Button type="submit" disabled={saving || !projectId || (isNew && (!np.name || !np.projected_sqft))}
            data-testid="save-to-project-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><FolderPlus className="mr-2 h-4 w-4" /> Save to project</>)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
